import { useField } from 'formik';
import { ReactNode, useState } from 'react';

import { FormAddableAutocomplete } from '../Fields/FormAutocomplete';

type AddableSelectProps = {
  name: string;
  label?: ReactNode;
  options?: { id: string; title: string }[];
  placeholder?: string;
  onChange?: (value, reason, details) => void;
  /**
   * if the value is not in the options, keep it.
   */
  keepUnmatched?: boolean;
};

export const FormAddableSelect = ({
  label,
  options: optionsProp = [],
  onChange,
  ...props
}: AddableSelectProps) => {
  const [, meta, helpers] = useField(props.name);
  const { value } = meta;
  const { setValue } = helpers;
  const [options, setOptions] =
    useState<{ id: string; title: string }[]>(optionsProp);

  const handleChange = (...args) => {
    const [value, reason, details] = args;
    let finalValue = [...(value || [])];

    if (
      reason === 'createOption' ||
      (reason === 'selectOption' && details?.option?.inputValue)
    ) {
      const rawValue =
        reason === 'createOption' ? details.option : details.option.inputValue;
      const o = (rawValue || '').trim();

      const newOption = {
        id: o,
        title: o,
      };

      if (!options.some((opt) => opt.id === o)) {
        setOptions((prev) => [...prev, newOption]);
      }

      finalValue = finalValue
        .filter((v) => {
          if (typeof v === 'string') {
            return v.trim() !== o;
          }
          if (v && typeof v === 'object') {
            if ('inputValue' in v && v.inputValue === o) {
              return false;
            }
            return v.id !== o;
          }
          return true;
        })
        .map((v) =>
          typeof v === 'string' ? { id: v.trim(), title: v.trim() } : v
        )
        .concat(newOption);
    }
    const v = finalValue.map((v) => (typeof v === 'string' ? v.trim() : v.id));
    setValue(v);
    onChange?.(v, reason, details);
  };

  return (
    <FormAddableAutocomplete
      {...props}
      label={label as any}
      value={value}
      options={options}
      multiple
      onChange={handleChange}
    />
  );
};
